import Link from "next/link";
import { FortschrittZelle } from "./tabelle/zellen";

/**
 * Karte mit Kopf (Titel + Anzahl + optionale Steuerung), Liste oder
 * Leerzustand und optionaler Fußzeile mit Absprung-Link.
 */
export function ListenKarte({
  id,
  titel,
  untertitel,
  anzahl,
  kopfAktion,
  leer,
  leerText,
  fussHref,
  fussText = "Alle ansehen",
  rahmenlos = false,
  children,
}: {
  id?: string;
  titel: string;
  /**
   * Die Karte sitzt schon in einem Rahmen (Reiter des rechten Panels der Live-Fläche, Variante C): ohne eigenen Rahmen, Schatten und Rundung (kein
   * Rahmen im Rahmen). Kopf, Liste und Fuß bleiben unverändert; additiv, ohne Angabe nichts anders.
   */
  rahmenlos?: boolean;
  /**
   * Zeile unter dem Titel im Kopf (`text-xs`, gedämpft), z. B. die Frist der Kantine-Tageskarte (Startseite v2,
   * AW-095 (1)); der Aufrufer setzt ein Symbol davor. Ohne Angabe bleibt der Kopf unverändert.
   */
  untertitel?: React.ReactNode;
  anzahl?: number;
  kopfAktion?: React.ReactNode;
  leer: boolean;
  leerText: string;
  /** Fußzeilen-Link; wird nur gerendert, wenn gesetzt. */
  fussHref?: string;
  fussText?: string;
  children?: React.ReactNode;
}) {
  const titelId = id ? `${id}-titel` : undefined;
  return (
    <>
      {/* Preline: dashboards/admin-dashboard/index.html#browsers-card */}
      {/* Abweichung AW-112 (3): `rahmenlos` für die Aufgaben-Karte im Reiter des Live-Panels */}
      <section id={id} aria-labelledby={titelId} className={rahmenlos ? "h-full flex flex-col" : "h-full flex flex-col bg-card border border-card-line shadow-2xs rounded-xl"}>
        {/* Header */}
        <div className="p-5 pb-3 flex flex-wrap justify-between items-center gap-2">
          {untertitel ? (
            <div>
              <h2 id={titelId} className="ms-1 inline-block text-lg font-semibold text-foreground">
                {titel}
              </h2>
              <p className="ms-1 mt-0.5 flex items-center gap-x-1.5 text-xs text-muted-foreground-1">{untertitel}</p>
            </div>
          ) : (
            <h2 id={titelId} className="ms-1 inline-block text-lg font-semibold text-foreground">
              {titel}
            </h2>
          )}
          <div className="flex items-center gap-x-2">
            {kopfAktion}
            {typeof anzahl === "number" && anzahl > 0 && (
              <span className="py-1 ps-1.5 pe-2 inline-flex items-center gap-x-1 text-xs font-medium rounded-full bg-layer border border-layer-line text-layer-foreground">
                {anzahl}
              </span>
            )}
          </div>
        </div>
        {/* End Header */}

        {/* Body */}
        {leer ? (
          <>
            {/* Preline: feedback-and-states/feedback-and-states-sub-alerts-recovery/feedback-and-states-cat-empty-states/no-events-empty-state-with-import-and-create-actions */}
            <div className="p-5 flex flex-col justify-center items-center text-center">
              <div className="max-w-md mx-auto">
                <p className="text-sm text-muted-foreground-1">{leerText}</p>
              </div>
            </div>
            {/* End Empty State */}
          </>
        ) : (
          <div className="h-full">{children}</div>
        )}
        {/* End Body */}

        {/* Footer */}
        {fussHref && (
          <div className="text-center border-t border-card-footer">
            <Link
              className="p-3 flex justify-center items-center gap-x-2 text-sm text-primary rounded-b-lg hover:text-primary-hover focus:outline-hidden focus:decoration-2 focus:underline focus:text-primary-focus"
              href={fussHref}
            >
              {fussText}
            </Link>
          </div>
        )}
        {/* End Footer */}
      </section>
    </>
  );
}

/** Zeilendichte der Listenkarten (Betreiber-Entscheid 05.10.: „Standard" / „Kompakt"). */
export type ListenDichte = "standard" | "kompakt";

/** Chevron wie im KPI-Karten-Fußlink (kpi-karte.tsx): Hinweis „Zeile führt weiter". */
export const ZEILEN_CHEVRON = (
  <svg className="shrink-0 size-4 text-muted-foreground-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/**
 * Listenzeile: Symbol-Kachel, Titel als Link, Metazeile, rechts optionale Zusätze.
 * Die GANZE Zeile ist klickbar: `li` ist `relative`, der Titel-Link spannt per
 * `after:absolute after:inset-0` seine Fläche über die Zeile (erweiterte Link-Fläche:
 * genau ein Anker, ein Tab-Stop). Eigenständige Bedienelemente in `abzeichen`/`rechts`
 * (Knopf, Link) tragen `relative z-10` und liegen über der Fläche; nie in einem Anker
 * verschachteln. Passive Teile (Symbol, Meta, Badges) fallen auf die Link-Fläche.
 * Liste: <ul className="py-2 px-5 overflow-hidden"> mit ListenZeile als <li>.
 */
export function ListenZeile({
  href,
  extern = false,
  icon,
  titel,
  meta,
  abzeichen,
  rechts,
  fortschritt,
  datumsblock,
  gedaempft = false,
  dichte = "standard",
  gewaehlt = false,
}: {
  /**
   * Gewählte Zeile in einer Liste | Detail (D4, M-100 Meine Aufgaben): getönter Grund (`bg-muted`) und `aria-current`; additiv,
   * ohne Angabe nichts anders. Die Wahl trägt nie allein die Aussage: das Detail daneben nennt dieselbe Zeile.
   */
  gewaehlt?: boolean;
  /**
   * Gedämpfte Zeile (additiv, Kantine „Allergene & Zusatzstoffe“, Filter „Ohne: …“, Betreiber 18.10.2026, F1): die Zeile bleibt
   * sichtbar und bedienbar, nur schwächer gezeichnet (`opacity-60`). Die Dämpfung trägt nie allein die Aussage: der Aufrufer nennt den
   * Grund im Text der Zeile („enthält Gluten“).
   */
  gedaempft?: boolean;
  /**
   * Ziel der Zeile. Fehlt es, steht der Titel als reiner Text ohne Anker und ohne Pfeil (additiv, Besprechungen M-023: Serien
   * ohne Leseweg für Lesende, Anbieten = Erlauben, r110); sonst unverändert.
   */
  href?: string;
  extern?: boolean;
  /** Symbol-Kachel; entfällt, wenn `datumsblock` gesetzt ist (dann ist `icon` überflüssig). */
  icon?: React.ReactNode;
  titel: string;
  meta?: React.ReactNode;
  /** Abzeichen direkt hinter dem Titel (z. B. Quelle, Schwere). */
  abzeichen?: React.ReactNode;
  /** Bereich am rechten Rand (Zusatztext oder Aktion). */
  rechts?: React.ReactNode;
  /**
   * Variante „Zeile mit Fortschritt“ (M-004 A2): Zahl + Balken rechts (ab `sm`); unter `sm` rutscht sie in die
   * Zweitzeile unter die Meta, damit der Name nicht gekürzt wird. Nur Fortschritt dieses Objekts, kein Zweigwert (TP4).
   */
  fortschritt?: { prozent: number; label: string };
  /**
   * Variante „mit Datumsblock“ (Besprechungen M-023, Design-Guide `listen-karte.md`): statt der Symbol-Kachel ein Block mit
   * Wochentag, Tag und Monat. Rein visuell (`aria-hidden`): das ausgeschriebene Datum steht in Titel oder Meta des Aufrufers.
   * Nur in Dichte „standard“; „kompakt“ hat keine Kachel und zeigt auch keinen Block.
   */
  datumsblock?: { wochentag: string; tag: string; monat: string };
  dichte?: ListenDichte;
}) {
  const titelKlasse = "text-sm font-medium text-foreground hover:text-primary focus:outline-hidden focus:text-primary after:absolute after:inset-0";
  if (dichte === "kompakt") {
    // Kompakt: keine Symbol-Kachel, Titel + Abzeichen + Meta in EINER Zeile (ohne Umbruch, gekürzt).
    const kompaktKlasse = `${titelKlasse} min-w-0 truncate`;
    return (
      <>
        {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/brand-logo-list */}
        <li
          className={
            gewaehlt
              ? gedaempft ? "relative py-1.5 -mx-2 px-2 rounded-lg bg-muted border-b last:border-b-0 border-line-2 opacity-60" : "relative py-1.5 -mx-2 px-2 rounded-lg bg-muted border-b last:border-b-0 border-line-2"
              : gedaempft ? "relative py-1.5 border-b last:border-b-0 border-line-2 opacity-60" : "relative py-1.5 border-b last:border-b-0 border-line-2"
          }
          aria-current={gewaehlt ? "true" : undefined}
        >
          <div className="flex items-center gap-x-3">
            <div className="grow min-w-0 flex items-center gap-x-2">
              {!href ? (
                <span className="min-w-0 truncate text-sm font-medium text-foreground">{titel}</span>
              ) : extern ? (
                <a className={kompaktKlasse} href={href} target="_blank" rel="noopener noreferrer">
                  {titel}
                </a>
              ) : (
                <Link className={kompaktKlasse} href={href}>
                  {titel}
                </Link>
              )}
              {abzeichen && <span className="shrink-0">{abzeichen}</span>}
              {meta && <span className="min-w-0 shrink-4 truncate text-xs text-muted-foreground-1">{meta}</span>}
            </div>
            {rechts && <div className="shrink-0 self-center">{rechts}</div>}
            {href && !extern && <span className="shrink-0 self-center">{ZEILEN_CHEVRON}</span>}
          </div>
        </li>
      </>
    );
  }
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/brand-logo-list */}
      <li
        className={
          gewaehlt
            ? gedaempft ? "relative py-3 -mx-2 px-2 rounded-lg bg-muted border-b last:border-b-0 border-line-2 opacity-60" : "relative py-3 -mx-2 px-2 rounded-lg bg-muted border-b last:border-b-0 border-line-2"
            : gedaempft ? "relative py-3 border-b last:border-b-0 border-line-2 opacity-60" : "relative py-3 border-b last:border-b-0 border-line-2"
        }
        aria-current={gewaehlt ? "true" : undefined}
      >
        <div className="flex gap-x-3">
          {datumsblock ? (
            <span className="mt-0.5 flex shrink-0 flex-col justify-center items-center w-12 py-1 bg-layer border border-layer-line text-layer-foreground rounded-lg" aria-hidden="true">
              <span className="text-xs uppercase text-muted-foreground-1">{datumsblock.wochentag}</span>
              <span className="text-lg font-semibold leading-tight text-foreground">{datumsblock.tag}</span>
              <span className="text-xs uppercase text-muted-foreground-1">{datumsblock.monat}</span>
            </span>
          ) : (
            <span className="mt-1 flex shrink-0 justify-center items-center size-9.5 bg-layer border border-layer-line text-layer-foreground rounded-lg" aria-hidden="true">
              {icon}
            </span>
          )}
          <div className="grow min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {!href ? (
                <span className="text-sm font-medium text-foreground">{titel}</span>
              ) : extern ? (
                <a className={titelKlasse} href={href} target="_blank" rel="noopener noreferrer">
                  {titel}
                </a>
              ) : (
                <Link className={titelKlasse} href={href}>
                  {titel}
                </Link>
              )}
              {abzeichen}
            </div>
            {meta && <div className="text-xs text-muted-foreground-1">{meta}</div>}
            {fortschritt && (
              <div className="mt-1.5 sm:hidden">
                <FortschrittZelle prozent={fortschritt.prozent} label={fortschritt.label} />
              </div>
            )}
          </div>
          {fortschritt && (
            <div className="hidden sm:flex shrink-0 self-center">
              <FortschrittZelle prozent={fortschritt.prozent} label={fortschritt.label} />
            </div>
          )}
          {rechts && <div className="shrink-0 self-center">{rechts}</div>}
          {href && !extern && <span className="shrink-0 self-center">{ZEILEN_CHEVRON}</span>}
        </div>
      </li>
    </>
  );
}

/**
 * Aktivitäten-Zeile (Uhrzeit, Titel, Text, Abzeichen). Mit `href` (geprüfter interner Pfad,
 * Prüfung beim Aufrufer) ist die GANZE Zeile ein Link (gleiche erweiterte Link-Fläche wie
 * ListenZeile, Chevron rechts); Uhrzeit, Text und Abzeichen bleiben passiv. Ohne `href`
 * unverlinkt, ohne Hinweis.
 */
export function AktivitaetsZeile({
  href,
  zeit,
  titel,
  text,
  abzeichen,
  dichte = "standard",
}: {
  href?: string;
  zeit: string;
  titel: string;
  text?: React.ReactNode;
  abzeichen?: React.ReactNode;
  dichte?: ListenDichte;
}) {
  if (dichte === "kompakt") {
    // Kompakt: Uhrzeit, Titel, Text und Abzeichen in EINER Zeile (ohne Umbruch, gekürzt).
    return (
      <>
        {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/brand-logo-list */}
        <li className="relative py-1.5 border-b last:border-b-0 border-line-2">
          <div className="flex items-center gap-x-3">
            <span className="shrink-0 text-xs text-muted-foreground-1 tabular-nums">{zeit}</span>
            <div className="grow min-w-0 flex items-center gap-x-2">
              {href ? (
                <Link className="min-w-0 truncate text-sm text-foreground hover:text-primary focus:outline-hidden focus:text-primary after:absolute after:inset-0" href={href}>
                  {titel}
                </Link>
              ) : (
                <span className="min-w-0 truncate text-sm text-foreground">{titel}</span>
              )}
              {text && <span className="min-w-0 shrink-4 truncate text-xs text-muted-foreground-1">{text}</span>}
            </div>
            {abzeichen && <div className="shrink-0 self-center">{abzeichen}</div>}
            {href && <span className="shrink-0 self-center">{ZEILEN_CHEVRON}</span>}
          </div>
        </li>
      </>
    );
  }
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/brand-logo-list */}
      <li className="relative py-3 border-b last:border-b-0 border-line-2">
        <div className="flex gap-x-3">
          <span className="shrink-0 text-xs text-muted-foreground-1 tabular-nums">{zeit}</span>
          <div className="grow min-w-0">
            <p className="text-sm text-foreground">
              {href ? (
                <Link className="hover:text-primary focus:outline-hidden focus:text-primary after:absolute after:inset-0" href={href}>
                  {titel}
                </Link>
              ) : (
                titel
              )}
            </p>
            {text && <p className="text-xs text-muted-foreground-1">{text}</p>}
          </div>
          {abzeichen && <div className="shrink-0 self-start">{abzeichen}</div>}
          {href && <span className="shrink-0 self-center">{ZEILEN_CHEVRON}</span>}
        </div>
      </li>
    </>
  );
}

/** Liste für ListenZeile. */
export function ListenGruppe({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/brand-logo-list */}
      <ul className="py-2 px-5 overflow-hidden">{children}</ul>
    </>
  );
}
